/**
 * Custom Framework Service
 * API integration layer for framework generation, validation, and management
 */
import { getApiBaseUrl } from '../utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

/** Auth headers helper — includes JWT token from localStorage */
const getAuthHeaders = (contentType = true) => {
  const headers = {
    'Authorization': `Bearer ${localStorage.getItem('token')}`
  };
  if (contentType) headers['Content-Type'] = 'application/json';
  return headers;
};

/**
 * Retry wrapper with exponential backoff
 * @param {Function} fn - Function to retry
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} baseDelay - Base delay in milliseconds
 * @returns {Promise} Result of the function
 */
async function retryWithBackoff(fn, maxRetries = 3, baseDelay = 1000) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries) throw error;
      
      const delay = baseDelay * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}

/**
 * Generate framework using AI
 * @param {Object} promptData - Framework configuration and prompt
 * @param {Function} onProgressUpdate - Callback for progress updates
 * @returns {Promise<Object>} Generated framework data
 */
/**
 * Detect domain and propose framework structure using AI
 * @param {Object} promptData - Framework configuration and prompt
 * @returns {Promise<Object>} Domain analysis and structure proposal
 */
export const detectDomainAndProposeStructure = async (promptData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/frameworks/generate/incremental/detect-domain`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        user_prompt: promptData.prompt,
        framework_description: promptData.description_en || '',
        framework_type: promptData.framework_type || 'CUSTOM',
        selected_standards: promptData.selected_standards || [],
        standards_reliance: promptData.standards_reliance || {},
        locale: promptData.locale || 'en'
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: Failed to detect domain`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Domain detection failed');
    }

    return {
      success: true,
      domain_analysis: data.domain_analysis,
      structure_proposal: data.structure_proposal,
      recommendations: data.recommendations || []
    };
  } catch (error) {
    console.error('❌ Domain detection error:', error);
    return {
      success: false,
      error: error.message || 'Failed to detect domain and propose structure'
    };
  }
};

export const generateFramework = async (promptData, onProgressUpdate = null) => {
  try {
    // Transform promptData to match backend API expectations
    const requestBody = {
      user_prompt: promptData.prompt,
      framework_code: promptData.framework_code,
      config: {
        framework_type: promptData.framework_type,
        name_en: promptData.name_en,
        name_ar: promptData.name_ar,
        description_en: promptData.description_en,
        description_ar: promptData.description_ar,
        structure_config: promptData.structure_config,  // Pass slider values!
        selected_standards: promptData.selected_standards || [],
        standards_reliance: promptData.standards_reliance || {} // Object with standard_id: reliance_level
      }
    };
    
      // Start session
      const startRes = await fetch(`${API_BASE_URL}/frameworks/generate/incremental/start`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });
      const startData = await startRes.json();
      if (!startRes.ok) throw new Error(startData.error || 'Failed to start incremental generation');

      // Handle both session_id and sessionId for compatibility
      const sessionId = startData.session_id || startData.sessionId;
      
      // ✅ NEW: Process micro-batches until complete
      // Each batch is small (1 prospective, 5 dimensions, or 10 criteria)
      // So we can poll more frequently without timeouts
      let allComplete = false;
      let attempts = 0;
      const MAX_ATTEMPTS = 1000;  // Increased for batch-based approach
      
      while (!allComplete && attempts < MAX_ATTEMPTS) {
        attempts++;
        
        try {
          // Execute ONE micro-batch
          const batchRes = await retryWithBackoff(async () => {
            const response = await fetch(
              `${API_BASE_URL}/frameworks/generate/incremental/execute/${sessionId}`,
              { method: 'POST', headers: getAuthHeaders(false) }
            );
            
            if (!response.ok) {
              let errorData = {};
              let errorMsg = `HTTP ${response.status}`;
              
              try {
                errorData = await response.json();
              } catch (parseError) {
                console.error('Failed to parse error response:', parseError);
              }
              
              // Safely extract error message with multiple fallbacks
              if (errorData) {
                if (typeof errorData.error === 'string') {
                  errorMsg = errorData.error;
                } else if (errorData.error && typeof errorData.error === 'object') {
                  errorMsg = errorData.error.message || JSON.stringify(errorData.error);
                } else if (errorData.message) {
                  errorMsg = errorData.message;
                }
              }
              
              console.error('❌ Batch execution failed:', {
                status: response.status,
                errorData,
                extractedMessage: errorMsg
              });
              
              throw new Error(errorMsg);
            }
            return response;
          });
          
          const batchData = await batchRes.json();

          // Get current status
          const statusRes = await fetch(
            `${API_BASE_URL}/frameworks/generate/incremental/status/${sessionId}`,
            { headers: getAuthHeaders(false) }
          );
          const statusData = await statusRes.json();
          
          // Update progress callback
          if (onProgressUpdate && statusData.session) {
            onProgressUpdate({
              session: statusData.session,
              progress: statusData.session.progress_data || {},
              total_items: statusData.session.total_items || {},
              current_phase: statusData.session.current_phase,
              batch_info: batchData.batch_result
            });
          }
          
          // Check if all batches complete
          allComplete = batchData.all_complete || false;
          
          if (allComplete) {
            break;
          }
          
          // Short delay between batches (500ms)
          await new Promise(r => setTimeout(r, 500));
          
        } catch (error) {
          console.error(`Batch ${attempts} failed:`, error);
          throw error;
        }
      }
      
      if (!allComplete) {
        throw new Error(`Generation timeout after ${attempts} batches`);
      }
      
      // Get complete framework
      const completeRes = await fetch(`${API_BASE_URL}/frameworks/generate/incremental/complete/${sessionId}`, {
        headers: getAuthHeaders(false)
      });
      const completeData = await completeRes.json();
      if (!completeRes.ok) throw new Error(completeData.error?.message || 'Failed to get complete framework');
      
      // Ensure framework-level names/descriptions strictly use Step 1 form values
      const enforcedFramework = {
        ...completeData.framework,
        name_en: requestBody.config.name_en,
        name_ar: requestBody.config.name_ar,
        description_en: requestBody.config.description_en || null,
        description_ar: requestBody.config.description_ar || null
      };

      // Return in the format expected by Step2_Generation
      return {
        data: enforcedFramework,
        validation: completeData.validation,
        statistics: completeData.statistics
      };
  } catch (error) {
    console.error('Generate framework error:', error);
    throw error;
  }
};

/**
 * Validate framework structure and content
 * @param {Object} framework - Framework object to validate
 * @returns {Promise<Object>} Validation results
 */
export const validateFramework = async (framework) => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/validate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ framework })
    });

    const data = await response.json();

    if (!response.ok) {
      // Validation endpoint returns 400 for validation failures, which is expected
      if (response.status === 400 && data.error?.validationErrors) {
        return data; // Return validation errors
      }
      throw new Error(data.error?.message || 'Failed to validate framework');
    }

    return data;
  } catch (error) {
    console.error('Validate framework error:', error);
    throw error;
  }
};

/**
 * Translate text to Arabic
 * @param {String} text - Text to translate
 * @param {String} context - Context for translation
 * @returns {Promise<Object>} Translation result
 */
export const translateText = async (text, context = '') => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/translate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ text, context })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to translate text');
    }

    return data;
  } catch (error) {
    console.error('Translate text error:', error);
    throw error;
  }
};

/**
 * Save framework to database
 * @param {Object} framework - Complete framework object
 * @returns {Promise<Object>} Saved framework data
 */
export const saveFramework = async (framework) => {
  try {
    const payload = { framework };

    const response = await fetch(`${API_BASE_URL}/custom-frameworks`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('Save framework failed:', data);
      throw new Error(data.error?.message || JSON.stringify(data.error) || 'Failed to save framework');
    }

    return data.data; // Return the actual saved framework data, not the wrapper
  } catch (error) {
    console.error('💥 Save framework error:', error);
    throw error;
  }
};

/**
 * List all frameworks
 * @param {String} status - Filter by status (active, draft, archived)
 * @param {Boolean} includeOrphaned - Include orphaned data from old tables
 * @returns {Promise<Array>} List of frameworks
 */
export const listFrameworks = async (status = 'active', includeOrphaned = true) => {
  try {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (includeOrphaned) params.append('include_orphaned', 'true');
    
    const url = `${API_BASE_URL}/custom-frameworks${params.toString() ? '?' + params.toString() : ''}`;

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to list frameworks');
    }

    return data.data; // Return the actual frameworks data, not the wrapper
  } catch (error) {
    console.error('List frameworks error:', error);
    throw error;
  }
};

/**
 * Get framework by code
 * @param {String} code - Framework code (e.g., 'HR', 'CS')
 * @returns {Promise<Object>} Framework data
 */
export const getFramework = async (code) => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/${code}`, {
      headers: getAuthHeaders(false)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to get framework');
    }

    return data.data; // Return the actual framework data, not the wrapper
  } catch (error) {
    console.error('Get framework error:', error);
    throw error;
  }
};

/**
 * Get complete framework with all hierarchical data
 * @param {String} code - Framework code (e.g., 'HR', 'CS')
 * @returns {Promise<Object>} Complete framework data with prospectives, dimensions, criteria, statements
 */
export const getCompleteFramework = async (code) => {
  try {
    // Get framework metadata
    const framework = await getFramework(code);

    // Get framework items (hierarchy)
    const items = await getFrameworkItems(code);

    // Get framework statements
    const statements = await getFrameworkStatements(code);

    // Build complete framework structure
    const completeFramework = {
      ...framework,
      prospectives: buildFrameworkStructure(items, statements)
    };
    
    return completeFramework;
  } catch (error) {
    console.error('Get complete framework error:', error);
    throw error;
  }
};

/**
 * Build framework structure from items and statements
 * @param {Array} items - Framework items from API
 * @param {Array} statements - Framework statements from API
 * @returns {Array} Structured framework with prospectives, dimensions, criteria, statements
 */
const buildFrameworkStructure = (items, statements) => {
  if (!items || items.length === 0) {
    return [];
  }

  // Group items by type
  const prospectives = items.filter(item => item.item_type === 'prospective');
  const dimensions = items.filter(item => item.item_type === 'dimension');
  const criteria = items.filter(item => item.item_type === 'criteria');
  
  // Build hierarchical structure
  const structuredProspectives = prospectives.map(prospective => {
    const prospectiveDimensions = dimensions
      .filter(dim => {
        // Dimensions should have parent_id pointing to the prospective
        const matchesParentId = dim.parent_id === prospective.id;
        return matchesParentId;
      })
      .map(dimension => {
        const dimensionCriteria = criteria
          .filter(crit => {
            // Criteria should have parent_id pointing to the dimension
            const matchesParentId = crit.parent_id === dimension.id;
            return matchesParentId;
          })
          .map(criterion => {
            const criterionStatements = statements
              .filter(stmt => stmt.criteria_id === criterion.id)
              .map(statement => ({
                id: statement.id,
                code: statement.code, // Use the actual code from database
                name_en: statement.name_en,
                name_ar: statement.name_ar,
                description_en: statement.description_en,
                description_ar: statement.description_ar,
                statement_en: statement.statement_en || statement.name_en, // Use statement_en or fallback to name_en
                statement_ar: statement.statement_ar || statement.name_ar, // Use statement_ar or fallback to name_ar
                order_sequence: statement.order_sequence
              }));
            
            return {
              id: criterion.id,
              code: criterion.code, // Use the actual code from database
              name_en: criterion.name_en,
              name_ar: criterion.name_ar,
              description_en: criterion.description_en,
              description_ar: criterion.description_ar,
              order_sequence: criterion.order_sequence,
              statements: criterionStatements
            };
          });
        
        return {
          id: dimension.id,
          code: dimension.code, // Use the actual code from database
          name_en: dimension.name_en,
          name_ar: dimension.name_ar,
          description_en: dimension.description_en,
          description_ar: dimension.description_ar,
          order_sequence: dimension.order_sequence,
          criteria: dimensionCriteria
        };
      });
    
    return {
      id: prospective.id,
      code: prospective.code, // Use the actual code from database
      name_en: prospective.name_en,
      name_ar: prospective.name_ar,
      description_en: prospective.description_en,
      description_ar: prospective.description_ar,
      order_sequence: prospective.order_sequence,
      dimensions: prospectiveDimensions
    };
  });
  
  return structuredProspectives;
};

/**
 * Get framework metadata/info
 * Alias for getFramework for clarity in display contexts
 * @param {String} code - Framework code
 * @returns {Promise<Object>} Framework metadata
 */
export const getFrameworkInfo = async (code) => {
  return getFramework(code);
};

/**
 * Get framework items (hierarchy)
 * @param {String} code - Framework code
 * @returns {Promise<Array>} Framework items
 */
export const getFrameworkItems = async (code) => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/${code}/items`, {
      headers: getAuthHeaders(false)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to get framework items');
    }

    return data.data; // Return the actual items data, not the wrapper
  } catch (error) {
    console.error('Get framework items error:', error);
    throw error;
  }
};

/**
 * Get framework statements
 * @param {String} code - Framework code
 * @returns {Promise<Array>} Framework statements
 */
export const getFrameworkStatements = async (code) => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/${code}/statements`, {
      headers: getAuthHeaders(false)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to get framework statements');
    }

    return data.data; // Return the actual statements data, not the wrapper
  } catch (error) {
    console.error('Get framework statements error:', error);
    throw error;
  }
};

/**
 * Update framework metadata
 * @param {Number} id - Framework ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated framework data
 */
export const updateFramework = async (id, updates) => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to update framework');
    }

    return data.data; // Return the actual updated framework data, not the wrapper
  } catch (error) {
    console.error('Update framework error:', error);
    throw error;
  }
};

/**
 * Delete framework
 * @param {Number} id - Framework ID
 * @returns {Promise<Object>} Delete confirmation
 */
export const deleteFramework = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(false)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to delete framework');
    }

    return data;
  } catch (error) {
    console.error('Delete framework error:', error);
    throw error;
  }
};

/**
 * Clear framework data by code (for regeneration)
 * @param {String} code - Framework code (e.g., 'HR', 'CS')
 * @param {Boolean} force - Force deletion even if in use
 * @returns {Promise<Object>} Delete confirmation
 */
export const clearFrameworkByCode = async (code, force = false) => {
  try {
    const url = force 
      ? `${API_BASE_URL}/custom-frameworks/clear/${code}?force=true`
      : `${API_BASE_URL}/custom-frameworks/clear/${code}`;
      
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(false)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to clear framework');
    }

    return data;
  } catch (error) {
    console.error('Clear framework error:', error);
    throw error;
  }
};

/**
 * Get framework statistics
 * @param {String} code - Framework code
 * @returns {Promise<Object>} Framework statistics
 */
export const getFrameworkStats = async (code) => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/${code}/stats`, {
      headers: getAuthHeaders(false)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to get framework stats');
    }

    return data.data; // Return the actual stats data, not the wrapper
  } catch (error) {
    console.error('Get framework stats error:', error);
    throw error;
  }
};

/**
 * Clone framework
 * @param {String} code - Original framework code
 * @param {String} newCode - New framework code
 * @param {String} newName - New framework name
 * @returns {Promise<Object>} Cloned framework data
 */
export const cloneFramework = async (code, newCode, newName) => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/${code}/clone`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ new_code: newCode, new_name: newName })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to clone framework');
    }

    return data.data; // Return the actual cloned framework data, not the wrapper
  } catch (error) {
    console.error('Clone framework error:', error);
    throw error;
  }
};

/**
 * Get all templates
 * @returns {Promise<Array>} List of templates
 */
export const getTemplates = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/templates`, {
      headers: getAuthHeaders(false)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to get templates');
    }

    return data.data; // Return the actual templates data, not the wrapper
  } catch (error) {
    console.error('Get templates error:', error);
    throw error;
  }
};

/**
 * Get single template
 * @param {String} code - Template code
 * @returns {Promise<Object>} Template data
 */
export const getTemplate = async (code) => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/templates/${code}`, {
      headers: getAuthHeaders(false)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to get template');
    }

    return data.data; // Return the actual template data, not the wrapper
  } catch (error) {
    console.error('Get template error:', error);
    throw error;
  }
};

/**
 * Use template to create framework
 * @param {String} code - Template code
 * @param {Object} params - Framework parameters
 * @returns {Promise<Object>} Generated framework
 */
export const useTemplate = async (code, params) => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/templates/${code}/use`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(params)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to use template');
    }

    return data;
  } catch (error) {
    console.error('Use template error:', error);
    throw error;
  }
};

/**
 * Insert sample HR framework data into staging tables
 * @returns {Promise<Object>} Session info and inserted counts
 */
export const insertSampleToStaging = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/sample/insert-staging`, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to insert sample framework to staging');
    }

    return data.data; // Return the actual data, not the wrapper
  } catch (error) {
    console.error('Insert sample to staging error:', error);
    throw error;
  }
};

/**
 * Migrate sample framework from staging to permanent tables and activate
 * @param {String} sessionId - Generation session ID
 * @returns {Promise<Object>} Migration result and framework details
 */
export const migrateSampleFramework = async (sessionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/sample/migrate-activate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ sessionId })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to migrate sample framework');
    }

    return data.data; // Return the actual data, not the wrapper
  } catch (error) {
    console.error('Migrate sample framework error:', error);
    throw error;
  }
};

/**
 * Get AI-suggested framework structure
 * @param {Object} promptData - Framework name, description, and prompt
 * @returns {Promise<Object>} Suggested structure with reasoning
 */
export const suggestStructure = async (promptData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/suggest-structure`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        name_en: promptData.name_en,
        name_ar: promptData.name_ar,
        description_en: promptData.description_en,
        description_ar: promptData.description_ar,
        prompt: promptData.prompt,
        framework_type: promptData.framework_type,
        selected_standards: promptData.selected_standards || [],
        standards_reliance: promptData.standards_reliance || {},
        locale: promptData.locale || 'en'
      })
    });
    
    // Check if response is ok before parsing JSON
    if (!response.ok) {
      let errorMessage = '';
      
      // Provide user-friendly messages for common HTTP errors
      if (response.status === 404) {
        errorMessage = 'AI suggestion feature is not yet available. The backend endpoint needs to be implemented. You can proceed by manually configuring the framework structure using the sliders below.';
      } else if (response.status === 500) {
        errorMessage = 'Server error occurred while getting AI suggestions. Please try again later or proceed manually.';
      } else if (response.status === 503) {
        errorMessage = 'AI service is temporarily unavailable. Please try again later or proceed manually.';
      } else {
        errorMessage = `Unable to get AI suggestions (${response.status}). Please try again or proceed manually.`;
      }
      
      // Try to get more details from JSON response if available
      try {
        const errorData = await response.json();
        if (errorData.error?.message) {
          errorMessage = errorData.error.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        // Response is not JSON, use the user-friendly message we set above
        console.warn('Non-JSON error response, using default message');
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();

    return {
      success: true,
      suggestions: data.suggestions || data.data
    };
  } catch (error) {
    console.error('Suggest structure error:', error);
    throw error;
  }
};

/**
 * Update framework item (prospective, dimension, or criteria)
 * @param {string} frameworkCode - Framework code
 * @param {number} itemId - Item ID to update
 * @param {Object} updates - Fields to update (name_en, name_ar, description_en, description_ar)
 * @returns {Promise<Object>} Updated item data
 */
export const updateFrameworkItem = async (frameworkCode, itemId, updates) => {
  try {
    const url = `${API_BASE_URL}/custom-frameworks/${frameworkCode}/items/${itemId}`;
    const token = localStorage.getItem('token');
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updates)
    });

    const data = await response.json();

    if (!response.ok) {
      // Handle different error response formats
      let errorMessage = 'Failed to update framework item';
      
      // Handle 404 specifically
      if (response.status === 404) {
        errorMessage = `API endpoint not found: ${url}. Please check if the server is running and the route is registered.`;
      } else if (data.error) {
        if (typeof data.error === 'string') {
          errorMessage = data.error;
        } else if (data.error.message) {
          errorMessage = data.error.message;
        } else if (data.error.error) {
          errorMessage = data.error.error;
        }
      } else if (data.message) {
        errorMessage = data.message;
      }
      
      console.error('❌ Update framework item error:', {
        status: response.status,
        statusText: response.statusText,
        url,
        errorData: data
      });
      throw new Error(errorMessage);
    }

    return data.data; // Return the updated item data
  } catch (error) {
    console.error('Update framework item error:', error);
    throw error;
  }
};

/**
 * Update framework statement
 * @param {string} frameworkCode - Framework code
 * @param {number} statementId - Statement ID to update
 * @param {Object} updates - Fields to update (statement_en, statement_ar, name_en, name_ar, description_en, description_ar)
 * @returns {Promise<Object>} Updated statement data
 */
export const updateFrameworkStatement = async (frameworkCode, statementId, updates) => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/${frameworkCode}/statements/${statementId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(updates)
    });

    const data = await response.json();

    if (!response.ok) {
      // Handle different error response formats
      let errorMessage = 'Failed to update framework statement';
      if (data.error) {
        if (typeof data.error === 'string') {
          errorMessage = data.error;
        } else if (data.error.message) {
          errorMessage = data.error.message;
        } else if (data.error.error) {
          errorMessage = data.error.error;
        }
      } else if (data.message) {
        errorMessage = data.message;
      }
      console.error('Update framework statement error response:', data);
      throw new Error(errorMessage);
    }

    return data.data; // Return the updated statement data
  } catch (error) {
    console.error('Update framework statement error:', error);
    throw error;
  }
};

/**
 * Delete framework item (prospective, dimension, or criteria) with cascade
 * @param {string} frameworkCode - Framework code
 * @param {number} itemId - Item ID to delete
 * @returns {Promise<Object>} Delete result
 */
export const deleteFrameworkItem = async (frameworkCode, itemId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/custom-frameworks/${frameworkCode}/items/${itemId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to delete framework item');
    }

    return data;
  } catch (error) {
    console.error('Delete framework item error:', error);
    throw error;
  }
};

export default {
  generateFramework,
  validateFramework,
  translateText,
  saveFramework,
  listFrameworks,
  getFramework,
  getCompleteFramework,
  getFrameworkInfo,
  getFrameworkItems,
  getFrameworkStatements,
  updateFramework,
  updateFrameworkItem,
  updateFrameworkStatement,
  deleteFramework,
  deleteFrameworkItem,
  clearFrameworkByCode,
  getFrameworkStats,
  cloneFramework,
  getTemplates,
  getTemplate,
  useTemplate,
  insertSampleToStaging,
  migrateSampleFramework,
  suggestStructure,
  detectDomainAndProposeStructure
};

